import React, { useEffect, useState, useCallback } from "react";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { MultiSelect } from "@/components/MultiSelect/MultiSelect";
import { DatePicker } from "@/components/DatePicker/DatePicker";
import { SelectWithOptions } from "@/components/SelectWithOptions/SelectWithOptions";
import { ContentBlockEditor } from "@/modules/hub/features/ResourceManagement/components/ContentBlockEditor/ContentBlockEditor";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { useParams } from "react-router-dom";
import { useEditResource } from "@/modules/hub/features/ResourceManagement/hooks/useEditResource/useEditResource";
import { useAppDispatch } from "@/app/hooks";
import { resetCreateUpdateResourceState } from "@/modules/hub/features/ResourceManagement/slices/createUpdateResourceSlice";
import { EntityMultiSelect } from "@/components/EntityMultiSelect/EntityMultiSelect";
import { getResourceCategories } from "@/modules/hub/features/ResourceCategoryManagement/api/getResourceCategories/getResourceCategoriesApi";
import { createResourceCategory } from "@/modules/hub/features/ResourceCategoryManagement/api/createResourceCategory/createResourceCategoryApi";
import { getResourceTags } from "@/modules/hub/features/ResourceTagManagement/api/getResourceTags/getResourceTagsApi";
import { createResourceTag } from "@/modules/hub/features/ResourceTagManagement/api/createResourceTag/createResourceTagApi";
import CreateUpdateResourceLoadingSkeleton from "@/modules/hub/features/ResourceManagement/components/CreateUpdateResourceLoadingSkeleton/CreateUpdateResourceLoadingSkeleton";
import { getResources } from "@/api/getResources/getResourcesApi";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { validateResourceSlug } from "@/modules/hub/features/ResourceManagement/api/validateResourceSlug/validateResourceSlugApi";
import { Check, XCircle } from "lucide-react";
import FilePickerDialog from "@/modules/hub/features/FileManagement/components/FilePickerDialog/FilePickerDialog";
import ThumbnailDisplay from "@/modules/hub/features/FileManagement/components/ThumbnailDisplay/ThumbnailDisplay";
import FileDisplay from "@/modules/hub/features/FileManagement/components/FileDisplay/FileDisplay";
import { getSeriesEligibleResources } from "@/modules/hub/features/ResourceManagement/api/getSeriesEligibleResources/getSeriesEligibleResourcesApi";
import { ResourceTypes } from "@/modules/hub/features/ResourceManagement/hooks/useCreateResource/types";
import {
  SortableSeriesEntries,
  SeriesEntryEntity,
} from "@/modules/hub/features/ResourceManagement/components/SortableSeriesEntries/SortableSeriesEntries";

interface RelatedResourceEntity {
  id: number;
  name: string;
  thumbnailUrl?: string | null;
  primaryIcon?: string | null;
  resourceType?: string | null;
  createdAt?: string;
}

export function EditResource() {
  const { uuid } = useParams<{ uuid: string }>();
  const dispatch = useAppDispatch();
  const {
    form,
    isLoading,
    submitForm,
    handleCancel,
    trades,
    roles,
    resourceTypes,
    loadingGet,
    metadataLoading,
    resourceTitle,
    fetchedResource,
    selectedResourceType,
  } = useEditResource(uuid);

  const { control, handleSubmit, formState, setValue, getValues, watch } = form;
  const { isSubmitting } = formState;

  // Track which submit action was triggered
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [clickedButton, setClickedButton] = useState<
    "save" | "saveAndClose" | null
  >(null);

  // Cleanup Redux state when component unmounts
  useEffect(() => {
    // Cleanup function to reset state when component unmounts
    return () => {
      dispatch(resetCreateUpdateResourceState());
    };
  }, [dispatch]);

  // Reset clicked button when submission completes
  useEffect(() => {
    if (!isSubmitting && !isLoading) {
      setClickedButton(null);
    }
  }, [isSubmitting, isLoading]);

  // File picker dialog state
  const [isFilePickerOpen, setIsFilePickerOpen] = useState(false);
  const [isMainFilePickerOpen, setIsMainFilePickerOpen] = useState(false);

  // Main file state for File type resources
  const [mainFileDisplayUrl, setMainFileDisplayUrl] = useState<string>("");
  const [mainFileName, setMainFileName] = useState<string>("");

  // Loading state for initial thumbnail load only
  const [loadingThumbnailUrl, setLoadingThumbnailUrl] = useState(false);

  // Track thumbnail display data separately from form data
  const [thumbnailDisplayUrl, setThumbnailDisplayUrl] = useState<string>("");
  const [thumbnailFileName, setThumbnailFileName] = useState<string>("");

  // Initialize thumbnail when resource is loaded
  useEffect(() => {
    const loadThumbnail = async () => {
      if (
        fetchedResource &&
        fetchedResource.thumbnailFileUuid &&
        !thumbnailDisplayUrl
      ) {
        try {
          setLoadingThumbnailUrl(true);

          // Use the thumbnail URL from the fetched resource
          // This will be the public URL if the file is public, or will require separate loading if private
          if (fetchedResource.thumbnailUrl) {
            setThumbnailDisplayUrl(fetchedResource.thumbnailUrl);
            setThumbnailFileName("Resource thumbnail");
          }
        } catch (error) {
          console.error("Error loading thumbnail:", error);
        } finally {
          setLoadingThumbnailUrl(false);
        }
      }
    };

    loadThumbnail();
  }, [fetchedResource, thumbnailDisplayUrl]);

  // Initialize main file for File type resources
  useEffect(() => {
    const loadMainFile = async () => {
      if (fetchedResource && fetchedResource.file && !mainFileDisplayUrl) {
        try {
          // Use the file URL from the fetched resource
          if (fetchedResource.file.url) {
            setMainFileDisplayUrl(fetchedResource.file.url);
            setMainFileName(fetchedResource.file.name || "Resource file");
          }
        } catch (error) {
          console.error("Error loading main file:", error);
        }
      }
    };

    loadMainFile();
  }, [fetchedResource, mainFileDisplayUrl]);

  const titleValue = watch("title") || "";

  // Conditional field visibility based on resource type
  const resourceTypeName = selectedResourceType?.name || "";
  const showContentUrl = [
    ResourceTypes.VIDEO,
    ResourceTypes.PODCAST,
    ResourceTypes.PARTNER_PAGE,
  ].includes(resourceTypeName as any);
  const requireContentUrl = [
    ResourceTypes.VIDEO,
    ResourceTypes.PODCAST,
    ResourceTypes.PARTNER_PAGE,
  ].includes(resourceTypeName as any);
  const showFileUpload = resourceTypeName === ResourceTypes.FILE;
  const showContentBlocks = resourceTypeName !== ResourceTypes.PARTNER_PAGE;
  const showSeriesEntries = resourceTypeName === ResourceTypes.SERIES;
  useEffect(() => {
    const autoSlug = titleValue
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
    setValue("slug", autoSlug, { shouldDirty: true });
  }, [titleValue, setValue]);

  const slugValue = watch("slug") || "";
  const debouncedSlug = useDebouncedValue(slugValue, 500);
  const [slugCheckStatus, setSlugCheckStatus] = useState<
    "idle" | "loading" | "valid" | "invalid"
  >("idle");
  const [slugCheckMessage, setSlugCheckMessage] = useState("");

  useEffect(() => {
    if (!debouncedSlug) {
      setSlugCheckStatus("idle");
      setSlugCheckMessage("");
      return;
    }
    setSlugCheckStatus("loading");
    validateResourceSlug({
      slug: debouncedSlug,
      resourceUuid: uuid || null,
    })
      .then((res) => {
        if (res.data.slugExists) {
          setSlugCheckStatus("invalid");
          setSlugCheckMessage(res.data.message || "Slug is already in use.");
        } else {
          setSlugCheckStatus("valid");
          setSlugCheckMessage("Slug is available!");
        }
      })
      .catch((err) => {
        console.error("Error validating slug:", err);
        setSlugCheckStatus("invalid");
        setSlugCheckMessage(
          "Error validating slug. Please try again or choose another.",
        );
      });
  }, [debouncedSlug, uuid]);

  // Handle thumbnail file selection
  const handleThumbnailSelected = useCallback(
    (
      files: Array<{
        fileUuid: string;
        fileUrl: string;
        publicUrl: string;
        name: string;
      }>,
    ) => {
      if (files.length === 0) return;

      const file = files[0]; // Take only the first file for thumbnail

      // Set the thumbnail URL for display (using the public URL)
      setThumbnailDisplayUrl(file.publicUrl);
      setThumbnailFileName(file.name);

      // Set the thumbnailFileUuid for the backend
      setValue("thumbnailFileUuid", file.fileUuid, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });

      // Close the file picker dialog
      setIsFilePickerOpen(false);
    },
    [setValue],
  );

  // Handle thumbnail removal
  const handleRemoveThumbnail = useCallback(() => {
    // Clear display data
    setThumbnailDisplayUrl("");
    setThumbnailFileName("");

    // Clear form data
    setValue("thumbnailFileUuid", null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
  }, [setValue]);

  // Handle main file selection for File type resources
  const handleMainFileSelected = useCallback(
    (
      files: Array<{
        fileUuid: string;
        fileUrl: string;
        publicUrl: string;
        name: string;
      }>,
    ) => {
      if (files.length === 0) return;

      const file = files[0];
      // Set the file URL for display (using the public URL)
      setMainFileDisplayUrl(file.publicUrl);
      setMainFileName(file.name);

      setValue("fileUuid", file.fileUuid, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      setIsMainFilePickerOpen(false);
    },
    [setValue],
  );

  // Handle main file removal
  const handleRemoveMainFile = useCallback(() => {
    setMainFileDisplayUrl("");
    setMainFileName("");

    setValue("fileUuid", null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [setValue]);

  if (loadingGet || metadataLoading) {
    return <CreateUpdateResourceLoadingSkeleton />;
  }

  return (
    <MainPageWrapper
      title="Edit Resource"
    >
      <Form {...form}>
        <form
          className="space-y-8 pb-24 bg-white"
          onSubmit={handleSubmit((values) =>
            submitForm(values, shouldRedirect),
          )}
        >
          <div className="text-sm text-muted-foreground mb-2">
            Fields marked with an asterisk (*) are required.
          </div>

          <FormField
            control={control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title *</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Slug *</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input {...field} />
                    {slugValue && slugCheckStatus === "valid" && (
                      <Check className="absolute right-2 top-1/2 -translate-y-1/2 text-green-500" />
                    )}
                    {slugValue && slugCheckStatus === "invalid" && (
                      <XCircle className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500" />
                    )}
                  </div>
                </FormControl>
                {slugCheckMessage && (
                  <p
                    className={`text-xs mt-1 ${
                      slugCheckStatus === "invalid"
                        ? "text-red-500"
                        : "text-green-500"
                    }`}
                  >
                    {slugCheckMessage}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="searchDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Search Description *</FormLabel>
                <FormDescription>
                  Internal field for search optimization and SEO. Not visible to
                  users.
                </FormDescription>
                <FormControl>
                  <Textarea {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Type *</FormLabel>
                <FormControl>
                  <SelectWithOptions
                    onValueChange={(val) => field.onChange(Number(val))}
                    options={resourceTypes.map((rt) => ({
                      label: rt.name.toUpperCase(),
                      value: String(rt.id),
                    }))}
                    value={field.value ? String(field.value) : ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {showContentUrl && (
            <FormField
              control={control}
              name="content_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Content URL {requireContentUrl && "*"}</FormLabel>
                  <FormControl>
                    <Input {...field} type="url" />
                  </FormControl>
                  <FormDescription>
                    {resourceTypeName === ResourceTypes.PARTNER_PAGE
                      ? "External URL that users will be redirected to"
                      : resourceTypeName === ResourceTypes.VIDEO
                        ? "Video URL (YouTube, Vimeo, etc.)"
                        : "Podcast URL (Transistor, Spotify, MP3, etc.)"}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          {showFileUpload && (
            <div>
              <FormLabel>File *</FormLabel>
              <FormDescription className="mb-2">
                Upload the main file for this resource (PDF, Word, Excel, etc.)
              </FormDescription>
              <div className="mt-2">
                <FileDisplay
                  fileName={mainFileName}
                  fileUrl={mainFileDisplayUrl}
                  loading={false}
                  onRemove={handleRemoveMainFile}
                  onSelect={() => setIsMainFilePickerOpen(true)}
                />
              </div>
            </div>
          )}

          <div>
            <FormLabel>Thumbnail</FormLabel>
            <FormDescription className="mb-2">
              Recommended thumbnail size is 192px x 192px.
            </FormDescription>
            {resourceTypeName === ResourceTypes.VIDEO && (
              <FormDescription className="mb-2 text-amber-600 dark:text-amber-400">
                Note: This thumbnail won't be displayed on the resource details page for videos
                (the video embed will show instead), but it's still important as it will be used
                in resource cards, related resources, and resource reference blocks.
              </FormDescription>
            )}
            <div className="mt-2">
              <ThumbnailDisplay
                loading={loadingThumbnailUrl}
                onRemove={handleRemoveThumbnail}
                onSelect={() => setIsFilePickerOpen(true)}
                thumbnailFileName={thumbnailFileName}
                thumbnailUrl={thumbnailDisplayUrl}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <FormField
              control={control}
              name="publish_start_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Publish Start Date</FormLabel>
                  <FormControl>
                    <DatePicker onChange={field.onChange} value={field.value} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="publish_end_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Publish End Date</FormLabel>
                  <FormControl>
                    <DatePicker
                      minDate={getValues("publish_start_date") || null}
                      onChange={field.onChange}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={control}
            name="is_published"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Published</FormLabel>
                  <FormDescription>
                    Make this resource available to users
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="requiresEaglesAccess"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    Requires Eagles Access
                  </FormLabel>
                  <FormDescription>
                    Only users with Eagles access can view this resource
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="requiresLATAAccess"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    Requires LATA Access
                  </FormLabel>
                  <FormDescription>
                    Only users with LATA subscription can view this resource
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FormField
              control={control}
              name="categories"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Categories</FormLabel>
                  <FormControl>
                    <EntityMultiSelect
                      createEntity={async ({ name }) => {
                        const res = await createResourceCategory({ name });
                        return {
                          id: res.data.id ?? 0,
                          name: res.data.name ?? "",
                        };
                      }}
                      entityNamePlural="Categories"
                      entityNameSingular="Category"
                      fetchEntities={async ({ searchTerm, page, pageSize }) => {
                        const response = await getResourceCategories({
                          name: searchTerm,
                          page,
                          pageSize,
                          sortBy: "name",
                          sortOrder: "ASC",
                        });
                        const { categories: catData } = response.data;
                        const totalCount =
                          response.meta?.totalCount ?? catData.length;
                        return {
                          data: catData.map((c) => ({
                            id: c.id,
                            name: c.name,
                          })),
                          totalCount,
                        };
                      }}
                      onChange={field.onChange}
                      value={field.value || []}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="tags"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tags (Internal)</FormLabel>
                  <FormControl>
                    <EntityMultiSelect
                      createEntity={async ({ name }) => {
                        const res = await createResourceTag({ name });
                        return {
                          id: res.data.id ?? 0,
                          name: res.data.name ?? "",
                        };
                      }}
                      entityNamePlural="Tags"
                      entityNameSingular="Tag"
                      fetchEntities={async ({ searchTerm, page, pageSize }) => {
                        const response = await getResourceTags({
                          name: searchTerm,
                          page,
                          pageSize,
                          sortBy: "name",
                          sortOrder: "ASC",
                        });
                        const { tags: tagData } = response.data;
                        const totalCount =
                          response.meta?.totalCount ?? tagData.length;
                        return {
                          data: tagData.map((t) => ({
                            id: t.id,
                            name: t.name,
                          })),
                          totalCount,
                        };
                      }}
                      onChange={field.onChange}
                      value={field.value || []}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FormField
              control={control}
              name="relatedResources"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Related Resources</FormLabel>
                  <FormControl>
                    <EntityMultiSelect<RelatedResourceEntity>
                      entityNamePlural="Resources"
                      entityNameSingular="Resource"
                      fetchEntities={async ({ searchTerm, page, pageSize }) => {
                        const response = await getResources({
                          searchTerm,
                          page,
                          pageSize,
                          sortBy: "title",
                          sortOrder: "ASC",
                        });
                        const totalCount =
                          response.meta?.totalCount ?? response.data.length;

                        return {
                          data: response.data.map((r) => ({
                            id: r.id,
                            name: r.title,
                            thumbnailUrl: r.thumbnailUrl,
                            primaryIcon: r.primaryIcon,
                            resourceType: r.resourceType,
                            createdAt: r.createdAt,
                          })),
                          totalCount,
                        };
                      }}
                      onChange={field.onChange}
                      renderEntityRow={(ent, isSelected, toggle) => {
                        return (
                          <div
                            className={`
                  cursor-pointer flex items-center gap-4 py-4 px-2 
                  border-b last:border-0
                  ${isSelected ? "bg-blue-50" : ""}
                `}
                            key={ent.id}
                            onClick={() => toggle(ent)}
                          >
                            {ent.thumbnailUrl ? (
                              <img
                                alt={ent.name}
                                className="object-cover w-16 h-16 rounded flex-shrink-0"
                                src={ent.thumbnailUrl}
                              />
                            ) : (
                              <div className="w-16 h-16 rounded flex items-center justify-center bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                                {ent.primaryIcon && (
                                  <span
                                    className="inline-block"
                                    dangerouslySetInnerHTML={{
                                      __html: ent.primaryIcon,
                                    }}
                                  />
                                )}
                              </div>
                            )}

                            <div className="flex-1">
                              <p className="font-medium hover:underline">
                                {ent.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {ent.resourceType} •{" "}
                                {ent.createdAt
                                  ? new Date(ent.createdAt).toLocaleDateString()
                                  : "No date"}
                              </p>
                            </div>
                            {isSelected && (
                              <Check className="h-5 w-5 text-primary flex-shrink-0" />
                            )}
                          </div>
                        );
                      }}
                      value={field.value || []}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {showSeriesEntries && (
            <FormField
              control={control}
              name="seriesEntries"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Series Videos/Podcasts</FormLabel>
                  <FormDescription>
                    Select and order the videos and podcasts in this series.
                    Drag to reorder.
                  </FormDescription>
                  <FormControl>
                    <SortableSeriesEntries
                      onChange={field.onChange}
                      value={field.value || []}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FormField
              control={control}
              name="tradeIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Trades</FormLabel>
                  <FormControl>
                    <MultiSelect
                      onChange={field.onChange}
                      options={trades.map((tr) => ({
                        label: tr.name,
                        value: String(tr.id),
                      }))}
                      placeholder="Select trades..."
                      value={field.value?.map(String) ?? []}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="roleIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Job Titles</FormLabel>
                  <FormControl>
                    <MultiSelect
                      onChange={field.onChange}
                      options={roles.map((r) => ({
                        label: r.name,
                        value: String(r.id),
                      }))}
                      placeholder="Select roles..."
                      value={field.value?.map(String) ?? []}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {showContentBlocks ? (
            <FormField
              control={control}
              name="contentBlocks"
              render={({ field }) => (
                <FormItem className="space-y-4">
                  <div className="flex flex-col space-y-2">
                    <FormLabel className="text-lg font-semibold">
                      Content Blocks
                    </FormLabel>
                    <FormDescription>
                      Add content blocks to structure your resource.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <ContentBlockEditor
                      blocks={field.value || []}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : resourceTypeName === ResourceTypes.PARTNER_PAGE ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-gray-400 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">
                    Content Blocks Not Available
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Partner Pages link directly to external websites or
                    platforms. Content blocks are not needed since users will be
                    redirected to the partner's site when they access this
                    resource.
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          <div className="sticky bottom-0 flex justify-end gap-4 px-4 py-4 border-t bg-white mt-8">
            <Button onClick={handleCancel} type="button" variant="outline">
              Cancel
            </Button>
            <Button
              disabled={isSubmitting || isLoading}
              onClick={() => {
                setShouldRedirect(false);
                setClickedButton("save");
              }}
              type="submit"
            >
              {(isSubmitting || isLoading) && clickedButton === "save"
                ? "Saving..."
                : "Save"}
            </Button>
            <Button
              disabled={isSubmitting || isLoading}
              onClick={() => {
                setShouldRedirect(true);
                setClickedButton("saveAndClose");
              }}
              type="submit"
              variant="default"
            >
              {(isSubmitting || isLoading) && clickedButton === "saveAndClose"
                ? "Saving..."
                : "Save & Close"}
            </Button>
          </div>
        </form>
      </Form>

      {/* File Picker Dialog for Thumbnail (single select) */}
      <FilePickerDialog
        allowedFileTypes={[
          "image/jpeg",
          "image/png",
          "image/gif",
          "image/webp",
        ]}
        isOpen={isFilePickerOpen}
        multiSelect={false}
        onClose={() => setIsFilePickerOpen(false)}
        onSelect={handleThumbnailSelected}
        title="Select Thumbnail"
      />

      {/* File Picker Dialog for Main File (File type resources) */}
      <FilePickerDialog
        allowedFileTypes={[
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/vnd.ms-excel",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-powerpoint",
          "application/vnd.openxmlformats-officedocument.presentationml.presentation",
          "text/plain",
          "text/csv",
        ]}
        isOpen={isMainFilePickerOpen}
        multiSelect={false}
        onClose={() => setIsMainFilePickerOpen(false)}
        onSelect={handleMainFileSelected}
        title="Select File"
      />
    </MainPageWrapper>
  );
}

export default EditResource;
